import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useQuery } from "@tanstack/react-query";
import { EditableGeocodedLocation, PickerPanel } from "@/components/common";
import { Button, Input, Modal, ModalFooter, PercentInput } from "@/components/ui";
import {
  emptyGeocodedLocation,
  type GeocodedLocationValue,
} from "@/lib/geocodedLocation";
import { useInvalidAddressSaveConfirm } from "@/hooks/useInvalidAddressSaveConfirm";
import type { AddressValidationState, HouseCreate } from "@/types";
import { salesAgentService } from "@/services/salesAgentService";
import { matchesTextQuery } from "@/lib/textSearch";
import { queryLoadFailure } from "@/lib/queryLoadFailure";
import { rateFromPercentInput } from "@/lib/commissionRate";

interface CreateHouseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Omit<HouseCreate, "host_user_id">) => void;
  isLoading: boolean;
}

type WizardStep = 1 | 2 | 3;

export function CreateHouseModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
}: CreateHouseModalProps) {
  const { t } = useTranslation("houses");
  const { t: tCommon } = useTranslation("common");
  const { t: tErrors } = useTranslation("errors");
  const [step, setStep] = useState<WizardStep>(1);
  const [name, setName] = useState("");
  const [location, setLocation] = useState<GeocodedLocationValue>(
    emptyGeocodedLocation,
  );
  const [addressValidation, setAddressValidation] =
    useState<AddressValidationState>({ status: "idle" });
  const [commissionPercent, setCommissionPercent] = useState("");
  const [salesAgentUserId, setSalesAgentUserId] = useState("");
  const [salesAgentSearch, setSalesAgentSearch] = useState("");
  const [salesAgentCommissionPct, setSalesAgentCommissionPct] = useState("");
  const [salesAgentCommissionError, setSalesAgentCommissionError] = useState<
    string | undefined
  >();

  const [nameError, setNameError] = useState<string | undefined>();
  const [commissionError, setCommissionError] = useState<string | undefined>();
  const [addressError, setAddressError] = useState<string | undefined>();

  const salesAgentsQuery = useQuery({
    queryKey: ["sales-agents", "house-create"],
    queryFn: () => salesAgentService.listForAdmin(),
    enabled: isOpen,
  });
  const { data: salesAgents = [] } = salesAgentsQuery;

  const salesAgentsDisplayed = useMemo(() => {
    return salesAgentSearch.trim()
      ? salesAgents.filter((sa) => matchesTextQuery(salesAgentSearch, [sa.email]))
      : salesAgents;
  }, [salesAgents, salesAgentSearch]);

  const clearFormState = useCallback(() => {
    setStep(1);
    setName("");
    setLocation(emptyGeocodedLocation());
    setAddressValidation({ status: "idle" });
    setCommissionPercent("");
    setSalesAgentUserId("");
    setSalesAgentSearch("");
    setSalesAgentCommissionPct("");
    setSalesAgentCommissionError(undefined);
    setNameError(undefined);
    setCommissionError(undefined);
    setAddressError(undefined);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      queueMicrotask(() => {
        clearFormState();
      });
    }
  }, [isOpen, clearFormState]);

  const handleClose = () => {
    clearFormState();
    onClose();
  };

  const handleStep1Next = () => {
    setNameError(undefined);
    setCommissionError(undefined);
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError(t("createModal.nameRequired"));
      return;
    }
    const commissionRate = rateFromPercentInput(
      String(commissionPercent).trim(),
    );
    if (commissionRate === null) {
      setCommissionError(t("createModal.commissionPercentRange"));
      return;
    }
    setStep(2);
  };

  const handleSkipSalesAgent = () => {
    setSalesAgentUserId("");
    setSalesAgentSearch("");
    setSalesAgentCommissionPct("");
    setSalesAgentCommissionError(undefined);
    setStep(3);
  };

  const handleStep2Next = () => {
    setSalesAgentCommissionError(undefined);
    if (salesAgentUserId) {
      const saRate = rateFromPercentInput(salesAgentCommissionPct.trim());
      if (saRate === null) {
        setSalesAgentCommissionError(t("createModal.salesAgentCutRequired"));
        return;
      }
    }
    setStep(3);
  };

  const { requestInvalidAddressSaveConfirm, confirmModal } =
    useInvalidAddressSaveConfirm();

  const submitNewHouse = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const rate = rateFromPercentInput(String(commissionPercent).trim());
    if (rate === null) {
      return;
    }

    const payload: Omit<HouseCreate, "host_user_id"> = {
      name: trimmed,
      address: location.address.trim() || undefined,
      coordinates: location.coordinates,
      commission_rate: rate,
    };
    if (salesAgentUserId) {
      const saRate = rateFromPercentInput(salesAgentCommissionPct.trim());
      if (saRate === null) {
        toast.error(t("createModal.salesAgentCutRequired"));
        setStep(2);
        return;
      }
      payload.sales_agent_user_id = salesAgentUserId;
      payload.sales_agent_commission_rate = saRate;
    }
    onSubmit(payload);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step !== 3) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    const rate = rateFromPercentInput(String(commissionPercent).trim());
    if (rate === null) {
      return;
    }

    if (!location.address.trim()) {
      setAddressError(t("createModal.addressRequired"));
      return;
    }
    setAddressError(undefined);

    if (!location.coordinates) {
      requestInvalidAddressSaveConfirm(
        true,
        addressValidation,
        tCommon("addressValidation.saveWarningHouse"),
        submitNewHouse,
      );
      return;
    }

    submitNewHouse();
  };

  const addressLabels = useMemo(
    () => ({
      search: tCommon("addressSearch.label"),
      searchPlaceholder: tCommon("addressSearch.placeholder"),
    }),
    [tCommon],
  );

  return (
    <Modal
      closeLabel={tCommon("close")}
      isOpen={isOpen}
      onClose={handleClose}
      title={t("createModal.title")}
      size="xl"
    >
      <form
        onSubmit={handleFormSubmit}
        className="space-y-5"
      >
        {step === 1 && (
          <div className="space-y-3">
            <Input
              id="house-create-name"
              type="text"
              label={t("createModal.houseNameLabel")}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) setNameError(undefined);
              }}
              placeholder={t("createModal.houseNamePlaceholder")}
              autoComplete="off"
              error={nameError}
            />
            <PercentInput
              id="house-create-commission-percent"
              label={t("createModal.commissionPercentLabel")}
              value={commissionPercent}
              onChange={(e) => {
                setCommissionPercent(e.target.value);
                if (commissionError) setCommissionError(undefined);
              }}
              required
              error={commissionError}
            />
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <p className="text-sm text-surface-700">
              {t("createModal.wizardStep2Lead")}
            </p>
            <p className="text-sm font-medium text-surface-800">
              {t("createModal.salesAgent")}
            </p>
            <PickerPanel
              searchPlaceholder={t("createModal.salesAgentSearchPlaceholder")}
              searchValue={salesAgentSearch}
              onSearchChange={setSalesAgentSearch}
              isLoading={salesAgentsQuery.isLoading}
              loadError={queryLoadFailure(
                salesAgentsQuery,
                tErrors("loadSalesAgents"),
              )}
              emptyMessage={
                salesAgentSearch.trim()
                  ? t("createModal.salesAgentNoResults")
                  : t("createModal.salesAgentListEmpty")
              }
              items={salesAgentsDisplayed.map((sa) => ({
                id: sa.user_id,
                title: sa.email,
                selected: salesAgentUserId === sa.user_id,
                onSelect: () => {
                  setSalesAgentUserId(sa.user_id);
                  setSalesAgentSearch(sa.email);
                  setSalesAgentCommissionPct("");
                  setSalesAgentCommissionError(undefined);
                },
              }))}
            />
            <PercentInput
              id="house-create-sales-agent-cut"
              label={t("createModal.salesAgentCutPercentLabel")}
              value={salesAgentCommissionPct}
              onChange={(e) => {
                setSalesAgentCommissionPct(e.target.value);
                if (salesAgentCommissionError) {
                  setSalesAgentCommissionError(undefined);
                }
              }}
              required={!!salesAgentUserId}
              error={salesAgentCommissionError}
            />
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <p className="text-sm text-surface-700">
              {t("createModal.wizardStep3Lead")}
            </p>
            <div className="space-y-4 rounded-xl border border-surface-200 bg-surface-50 p-4">
              <EditableGeocodedLocation
                value={location}
                onChange={(next) => {
                  setLocation(next);
                  if (addressError) setAddressError(undefined);
                }}
                labels={addressLabels}
                syncKey={`house-create-step-${step}`}
                error={addressError}
                onValidationChange={setAddressValidation}
                layout="stack"
              />
            </div>
          </div>
        )}

        <ModalFooter className="-mx-6 -mb-5 mt-6">
          {step === 1 && (
            <>
              <Button
                type="button"
                variant="ghost"
                onClick={handleClose}
                disabled={isLoading}
              >
                {tCommon("cancel")}
              </Button>
              <Button
                type="button"
                onClick={handleStep1Next}
                disabled={isLoading}
              >
                {t("createModal.wizardNext")}
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setStep(1)}
                disabled={isLoading}
              >
                {tCommon("back")}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleSkipSalesAgent}
                disabled={isLoading}
              >
                {t("createModal.wizardSkip")}
              </Button>
              <Button
                type="button"
                onClick={handleStep2Next}
                disabled={isLoading}
              >
                {t("createModal.wizardNext")}
              </Button>
            </>
          )}
          {step === 3 && (
            <>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setStep(2)}
                disabled={isLoading}
              >
                {tCommon("back")}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={handleClose}
                disabled={isLoading}
              >
                {tCommon("cancel")}
              </Button>
              <Button
                type="submit"
                isLoading={isLoading}
                disabled={isLoading || !name.trim() || !location.address.trim()}
              >
                {t("createModal.create")}
              </Button>
            </>
          )}
        </ModalFooter>
      </form>
      {confirmModal}
    </Modal>
  );
}
